import { Component, effect, inject, OnInit, untracked } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { injectParams } from 'ngxtension/inject-params';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/shared/services/auth/auth_service';
import { MixpanelService } from 'src/shared/services/mix-panel.service/mix-panel.service';
import { ValidationService } from 'src/shared/services/validation/validation.service';
import { BannerComponent } from '../../design/banner/banner.component';
import { CenterTextButton } from '../../design/centertextbutton/centertextbutton.component';
import { RightImagePageWrapperComponent } from '../../design/rightimagepagewrapper/rightimagepagewrapper.component';
import { RightTextButton } from '../../design/righttextbutton/righttextbutton.component';
import { TitleComponent } from '../../design/title/title.component';
import { FormSubmissionButtonComponent } from '../../forms/form-submission-button/form-submission-button.component';
import { InputComponent } from '../../forms/input/input.component';
import { LucideDynamicIcon, LucideEye, LucideEyeOff } from '@lucide/angular';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TitleComponent,
    BannerComponent,
    InputComponent,
    TranslateModule,
    RightTextButton,
    FormSubmissionButtonComponent,
    CenterTextButton,
    RightImagePageWrapperComponent,
    LucideDynamicIcon,
  ],
})
export class LoginComponent implements OnInit {
  protected readonly EyeIcon = LucideEye;
  protected readonly EyeOffIcon = LucideEyeOff;

  private router = inject(Router);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private mixpanelService = inject(MixpanelService);
  private authenticationService = inject(AuthenticationService);

  providerParam = injectParams('provider');
  environment = environment;
  activeTenant = this.authenticationService.tenant;

  setActiveTenantEffect = effect(() => {
    const provider = this.providerParam();
    if (provider) {
      this.authenticationService.setTenant(provider);
    }
    untracked(() => {
      if (provider !== this.activeTenant()?.friendly_id) {
        this.authenticationService.updateTenant();
        this.mixpanelService.track('login', { business: this.activeTenant()?.friendly_id });
      }
      if (provider !== this.authenticationService.getTenantById(provider ?? '')?.friendly_id) {
        this.authenticationService.setEntitlementProvider(provider ?? '');
        this.authenticationService.updateEntitlementProvider();
      }
    });
  });

  ngOnInit(): void {
    this.mixpanelService.track('login', { business: this.activeTenant()?.friendly_id });

    const tenant = this.activeTenant();
    if (tenant?.sso && !tenant.allowLogin) {
      this.router.navigate(['/start'], { queryParams: { sso: tenant.friendly_id } });
    }
  }

  form = this.fb.group({
    email: ['', [ValidationService.validateEmail]],
    password: ['', [ValidationService.validateLoginPassword]],
  });
  loading!: boolean;
  error!: string;
  providerswitcher: boolean = environment.providerswitcher;
  showPassword: boolean = false;

  get loginImageSrc(): string {
    const defaultImage =
      'https://firebasestorage.googleapis.com/v0/b/vetai-e3e47.appspot.com/o/joii%2FLogin_pageimage.png?alt=media&token=b27d1a8b-9a2d-427f-8c24-2a51bf5b87de';
    const callcentreImage =
      'https://firebasestorage.googleapis.com/v0/b/core-6p22-ukprod-658c405b.appspot.com/o/public%2Fb4ccefd2d3b24cf8d19a2d818caa3fea65b0ea26.jpg?alt=media&token=a6584408-c908-477a-b54a-e61c61f304b3';

    if (this.activeTenant()?.useCallcentreLoginImage) {
      return callcentreImage;
    }
    return defaultImage;
  }

  onSubmit(form: FormGroup) {
    if (!form.valid) return;
    if (this.loading) return;

    this.error = '';
    this.loading = true;
    this.authenticationService
      .loginWithEmailAndPassword(form.value.email, form.value.password)
      .then(() => this.next())
      .catch((error) => {
        this.mixpanelService.track('login.failed', { business: this.activeTenant()?.friendly_id });
        this.handlerError(error.message);
      })
      .finally(() => this.resetForm());
  }

  register() {
    const provider = this.providerParam();
    if (provider) {
      this.router.navigate(['/register', provider], { queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate(['/register'], { queryParamsHandling: 'preserve' });
    }
  }

  forgottenpassword() {
    this.router.navigate(['/forgottenpassword'], {
      queryParamsHandling: 'preserve',
    });
  }

  anothertenant() {
    this.router.navigate(['/tenants/login'], {
      queryParamsHandling: 'preserve',
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  async next() {
    this.mixpanelService.track('login.success', { business: this.activeTenant()?.friendly_id });

    if (this.route.snapshot.queryParamMap.get('whatsappauth')) {
      this.redirectToUrl(`/whatsapp/petpicker?whatsappauth=${this.route.snapshot.queryParamMap.get('whatsappauth')}`);
      return;
    }

    const url = this.route.snapshot.queryParamMap.get('redirectTo') ?? '';
    const decodedUrl = decodeURIComponent(url);
    if (decodedUrl) {
      this.redirectToUrl(decodedUrl);
    } else {
      this.router.navigate([environment.afterAuthPath]);
    }
  }

  handlerError(error: string) {
    this.error = error;
  }

  clearError() {
    this.error = '';
  }

  resetForm() {
    this.loading = false;
  }

  google() {
    this.authenticationService
      .loginWithGoogle()
      .then(() => this.next())
      .catch((error) => this.handlerError(error.message))
      .finally(() => this.resetForm());
  }

  googleWithRedirect() {
    this.authenticationService
      .loginWithGoogleWithRedirect()
      .then(() => this.next())
      .catch((error) => this.handlerError(error.message))
      .finally(() => this.resetForm());
  }

  redirectToUrl(url: string) {
    const parsedUrl = new URL(url, window.location.origin);

    const route = parsedUrl.pathname;

    const queryParams: { [key: string]: string } = {};
    parsedUrl.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    this.router.navigate([route], { queryParams });
  }
}
